/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, forwardRef, useEffect } from "react";
import useEffectAsync, { useIsMounted } from "lib/react/customHooks";
import MaterialTable from "material-table";
import { Chip } from "@material-ui/core";

import {
  ArrowDownward,
  ChevronLeft,
  ChevronRight,
  Clear,
  Check,
  DeleteOutline,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  Search,
  ViewColumn
} from "@material-ui/icons";
import SettingsBackupRestoreIcon from "@material-ui/icons/SettingsBackupRestore";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import {
  getUsers,
  partialUpdateUser,
  getUserSpecificTenants
} from "lib/services/users";
import { getRoles } from "lib/services/roles";
import _ from "lodash";
import {
  RolesAutocomplete,
  CompaniesAutocomplete,
  AthenaTenantsAutocomplete
} from "app/utils/autocomplete";
import UserCompanyLabel from "app/utils/UserCompanyLabel";
import DeleteUserComponent from "./DeleteUserComponent";
import ResetPasswordComponent from "./ResetPasswordComponent";
import ValidateEmailComponent from "./ValidateEmailComponent";

// These cell components are defined at module scope (not inside UsersTable) on
// purpose: nesting them in the parent would give them a brand-new identity on
// every render, forcing React to unmount/remount each cell and re-fire its
// data fetches. That churn is what made the page slow down while editing rows.

// MaterialTable remounts every body cell whenever a row is edited/saved, which
// otherwise re-fires these per-row requests each time (and, since the responses
// land on now-unmounted components, leaks "state update on an unmounted
// component" warnings). Caching the results by user id keeps a save from
// stampeding the API again; the caches are cleared when the full user list is
// (re)fetched. Fetches are guarded with useIsMounted so a late response never
// updates a component that has already gone away.
export const userRolesCache = new Map();
export const userTenantsCache = new Map();

const UserRoles = ({ user }) => {
  const isMounted = useIsMounted();
  const [roles, setRoles] = useState(
    () => user.roles || userRolesCache.get(user.id) || []
  );

  useEffect(() => {
    if (user.roles) {
      userRolesCache.set(user.id, user.roles);
      setRoles(user.roles);
      return;
    }
    if (userRolesCache.has(user.id)) {
      setRoles(userRolesCache.get(user.id));
      return;
    }
    getRoles({ user_id: user.id })
      .then(response => {
        userRolesCache.set(user.id, response.data);
        if (isMounted()) setRoles(response.data);
      })
      .catch(() => {
        /* leave roles empty on failure rather than crashing the cell */
      });
  }, [user.id]);

  return (
    <div>
      {roles.map(role => (
        <Chip key={role.id} label={role.name} variant="outlined" />
      ))}
    </div>
  );
};

const UserSpecificTenants = ({ user }) => {
  const isMounted = useIsMounted();
  const [specificTenants, setSpecificTenants] = useState(
    () => user.athenaTenants || userTenantsCache.get(user.id) || []
  );

  useEffect(() => {
    if (user.athenaTenants) {
      userTenantsCache.set(user.id, user.athenaTenants);
      setSpecificTenants(user.athenaTenants);
      return;
    }
    if (userTenantsCache.has(user.id)) {
      setSpecificTenants(userTenantsCache.get(user.id));
      return;
    }
    getUserSpecificTenants(user.id)
      .then(response => {
        userTenantsCache.set(user.id, response.data);
        if (isMounted()) setSpecificTenants(response.data);
      })
      .catch(() => {
        /* leave tenants empty on failure rather than crashing the cell */
      });
  }, [user.id]);

  return (
    <div>
      {specificTenants.map(tenant => (
        <Chip
          key={tenant.id}
          label={tenant.mysql_database}
          variant="outlined"
        />
      ))}
    </div>
  );
};

const UserRolesAutocomplete = ({ user, onChange }) => {
  const [roles, setRoles] = useState(user.roles);

  useEffectAsync(
    async () => {
      const response = await getRoles({ user_id: user.id });
      return response.data;
    },
    setRoles,
    []
  );

  return roles ? (
    <RolesAutocomplete selectedRoles={roles} onChange={onChange} />
  ) : null;
};

const UserSpecificTenantsAutocomplete = ({ user, onChange }) => {
  const [tenants, setTenants] = useState(user.athenaTenants);

  useEffectAsync(
    async () => {
      const response = await getUserSpecificTenants(user.id);
      return response.data;
    },
    setTenants,
    []
  );

  return tenants ? (
    <AthenaTenantsAutocomplete
      selectedAthenaTenants={tenants}
      onChange={onChange}
    />
  ) : null;
};

// Stable module-scope reference: recreating these 16 forwardRef components on
// every render handed MaterialTable a new `icons` prop each time, forcing extra
// re-render work during edits.
const tableIcons = {
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  SettingsBackupRestoreIcon: forwardRef((props, ref) => (
    <SettingsBackupRestoreIcon {...props} ref={ref} />
  )),
  MailOutlineIcon: forwardRef((props, ref) => (
    <MailOutlineIcon {...props} ref={ref} />
  )),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />)
};

export default function UsersTable({ refreshKey /* , userPool */ }) {
  const [isLoading, setIsLoading] = useState(false);
  const initialRefresh = 0;
  const [refresh, setRefresh] = useState(initialRefresh);

  useEffect(() => {
    // Do not update refresh key on initial mount
    if (refreshKey !== initialRefresh) setRefresh(refresh + 1);
  }, [refreshKey]);

  const [state, setState] = useState({
    columns: [
      { title: "ID", field: "id", editable: "never" },
      { title: "Email", field: "email", editable: "never" },
      {
        title: "Company",
        field: "company_id",
        render: rowData => <UserCompanyLabel companyId={rowData.company_id} />,
        editComponent: props => {
          const { rowData, onChange } = props;
          return (
            <CompaniesAutocomplete
              selectedCompanyId={rowData.company_id}
              allowEmpty
              onChange={company => onChange(company ? company.id : null)}
            />
          );
        },
        width: 250
      },
      {
        title: "Roles",
        field: "roles",
        render: rowData => <UserRoles user={rowData} />,
        editComponent: props => {
          const { rowData, onChange } = props;
          return <UserRolesAutocomplete user={rowData} onChange={onChange} />;
        },
        width: 220
      },
      {
        title: "User Status",
        field: "user_state",
        render: rowData => (
          <Chip label={rowData.user_state} variant="outlined" />
        ),
        editable: "never"
      },
      {
        title: "Specific Tenants",
        field: "athenaTenants",
        render: rowData => <UserSpecificTenants user={rowData} />,
        editComponent: props => {
          const { rowData, onChange } = props;
          return (
            <UserSpecificTenantsAutocomplete
              user={rowData}
              onChange={onChange}
            />
          );
        },
        width: 220
      }
    ],
    data: []
  });

  useEffectAsync(
    async () => {
      setIsLoading(true);
      const response = await getUsers();
      return response.data;
    },
    users => {
      // Fresh list -> drop any stale per-row roles/tenants we had cached.
      userRolesCache.clear();
      userTenantsCache.clear();
      setState({
        ...state,
        data: users
      });
      setIsLoading(false);
    },
    [refresh /* , userPool */]
  );

  const [deleteDialogState, setDeleteDialogState] = useState({
    open: false
  });

  const [resetPassswordDialogState, setResetPassswordDialogState] = useState({
    open: false
  });

  const [ValidateEmailDialogState, setValidateEmailDialogState] = useState({
    open: false
  });

  const closeResetPassword = () => {
    setResetPassswordDialogState({ open: false });
  };

  const closeValidateEmail = () => {
    setValidateEmailDialogState({ open: false });
  };

  const closeDeleteDialog = () => {
    setDeleteDialogState({ open: false });
  };

  const handleDeleteClick = (_event, user) => {
    setDeleteDialogState({
      open: true,
      user
    });
  };

  const handleResetPasswordOnClick = (_event, user) => {
    setResetPassswordDialogState({
      open: true,
      user
    });
  };

  const handleValidateEmailOnClick = (_event, user) => {
    setValidateEmailDialogState({
      open: true,
      user
    });
  };

  const refreshTable = () => {
    setRefresh(refresh + 1);
  };

  const onPasswordResetSuccess = () => {
    refreshTable();
    closeResetPassword();
  };

  const onDeleteUserSuccess = () => {
    refreshTable();
    closeDeleteDialog();
  };

  return (
    <>
      <MaterialTable
        title="Users"
        // material-table mutates the column objects it is given: on every data
        // update it rewrites column.tableData.width, wrapping the previous
        // value in another css calc(). Feeding it the same objects from state
        // makes that string grow exponentially (each save got ~3x slower until
        // the app froze), so hand it disposable clones and keep ours pristine.
        columns={state.columns.map(column => ({ ...column }))}
        data={state.data}
        icons={tableIcons}
        isLoading={isLoading}
        options={{
          actionsColumnIndex: -1
        }}
        editable={{
          onRowUpdate: async (newData, oldData) => {
            const data = [...state.data];
            data[oldData.tableData.id] = newData;
            setState({
              ...state,
              data
            });

            const ignoreProps = {
              id: true,
              _links: true,
              company: true,
              email: true
            };
            const updateUser = {};

            _.forEach(newData, (value, prop) => {
              if (newData[prop] !== oldData[prop] && !(prop in ignoreProps)) {
                switch (prop) {
                  case "roles":
                    updateUser.role_ids = _.map(value, role => role.id);
                    break;
                  case "athenaTenants":
                    updateUser.athena_tenant_ids = _.map(
                      value,
                      tenant => tenant.id
                    );
                    break;
                  default:
                    updateUser[prop] = newData[prop];
                }
              }
            });

            if (!_.isEmpty(updateUser)) {
              partialUpdateUser({
                ...updateUser,
                id: newData.id
              });
            }
          }
        }}
        actions={[
          {
            icon: DeleteOutline,
            tooltip: "Delete user",
            onClick: handleDeleteClick
          },
          {
            icon: SettingsBackupRestoreIcon,
            tooltip: "Reset Password",
            onClick: handleResetPasswordOnClick
          },
          {
            icon: MailOutlineIcon,
            tooltip: "Validate email",
            onClick: handleValidateEmailOnClick
          }
        ]}
      />
      <DeleteUserComponent
        open={deleteDialogState.open}
        user={deleteDialogState.user}
        onSuccess={onDeleteUserSuccess}
        onDismiss={closeDeleteDialog}
        // userPool={userPool}
      />
      <ResetPasswordComponent
        open={resetPassswordDialogState.open}
        user={resetPassswordDialogState.user}
        onSuccess={onPasswordResetSuccess}
        onDismiss={closeResetPassword}
      />
      <ValidateEmailComponent
        open={ValidateEmailDialogState.open}
        user={ValidateEmailDialogState.user}
        onSuccess={onPasswordResetSuccess}
        onDismiss={closeValidateEmail}
      />
    </>
  );
}
